import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import firebase from 'firebase/app';
import { Redirect } from 'react-router-dom';
import qs from 'qs';

import StrikeClientModel from '../../../models/StrikeClientModel';
import { EditClientProfileForm } from '../../../components/client/EditClientProfileForm';
import SignupPhoneVerify from '../../../components/client/SignupPhoneVerify';
import SignupShell, { signupTextLink } from '../../../components/client/SignupShell';
import TrackerService from '../../../utils/TrackerService';
import CenteredSpinner from '../../../components/CenteredSpinner';
import { useCurrentUser } from '../../../models/useCurrentUser';
import { useClient } from '../../../models/useClient';
import {
  getAuthProviderFields, isPhoneOnlyAuthUser, signupStepsTotal,
} from '../../../utils/completeSignupHelpers';
import { MissingAccountScreen, CouldNotLoadScreen } from '../../../components/client/CompleteSignupFallbacks';

import { clearSessionForDashboard } from './ClientLoginScreen';

// Re-exported for the existing imports and tests.
export { isPhoneOnlyAuthUser, signupStepsTotal };

import '../../../App.scss';
import 'antd/dist/antd.css';

export default function CompleteClientSignup(props) {
  const clientId = props.match?.params?.clientId;
  const isAdmin = !!props.isAdmin;
  const queryParams = qs.parse(props.location?.search || '', { ignoreQueryPrefix: true });

  const [authLoading, authUser] = useCurrentUser();
  const [clientLoading, client, clientError] = useClient(clientId);

  // Verified mobile is a required, dedicated step BEFORE the profile form (only
  // reached by Google/Apple/email sign-ups — phone-first sign-ups already have it).
  // Bumped after SignupPhoneVerify links the number (linkWithCredential doesn't
  // reliably re-fire the auth subscription, so we re-read currentUser directly).
  const [phoneVerifiedTick, setPhoneVerifiedTick] = useState(0);
  // True while "Log in to that account instead" is signing out on purpose: the
  // signed-out bounce below must not fire first and send the login screen a
  // completeSignup redirect (which it would read as a join door, sign-up mode).
  const [leaving, setLeaving] = useState(false);
  const verifiedAuthPhone = useMemo(
    () => (firebase.auth().currentUser ? firebase.auth().currentUser.phoneNumber : null),
    [authUser, phoneVerifiedTick],
  );

  const modelRef = useRef(null);
  if (!modelRef.current && clientId) {
    modelRef.current = new StrikeClientModel({ viewModel: {}, clientId, isAdmin });
  }
  const model = modelRef.current;

  useEffect(() => {
    return () => { if (model) model.stop(); };
  }, [model]);

  const redirect = queryParams.redirect;
  const originReason = queryParams.originReason || null;
  const originPath = queryParams.originPath || null;
  const originParams = queryParams.originParams || null;
  const stepsTotal = signupStepsTotal(redirect || originPath);

  const resolveRedirectFromOriginParams = useCallback(() => {
    try {
      const raw = originParams;
      if (!raw) return null;
      const parsed = typeof raw === 'string' ? JSON.parse(decodeURIComponent(raw)) : raw;
      return parsed?.redirect || null;
    } catch {
      return null;
    }
  }, [originParams]);

  const applyIPAddressWhenTermsAccepted = useCallback(async (timeoutMs) => {
    if (!clientId) return;
    const isLocalHost = window.location.hostname === 'localhost';
    const requestTimeoutMs = typeof timeoutMs === 'number' ? timeoutMs : (isLocalHost ? 5000 : 1500);
    const baseUrl = isLocalHost ? 'http://localhost:8088' : 'https://pay.strikepay.co';
    const url = `${baseUrl}/api/clients/${clientId}/acceptedterms`;
    let timeoutId = null;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('acceptedterms IP save timed out')), requestTimeoutMs);
    });
    try {
      const response = await Promise.race([
        fetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: '{}' }),
        timeoutPromise,
      ]);
      if (!response?.ok) throw new Error(`acceptedterms failed: ${response?.status}`);
    } catch (error) {
      console.warn('[CompleteClientSignup] acceptedterms:', error?.message);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }, [clientId]);

  const onClickSave = useCallback((data) => {
    const isLocalHost = window.location.hostname === 'localhost';
    const authProviderFields = getAuthProviderFields(authUser);
    const dataWithAuthProvider = {
      ...data,
      ...authProviderFields,
      platform: 'web',
      originReason,
      originPath,
      originParams,
    };

    (async () => {
      if (model && client) {
        model.client = client;
        await model.commit(dataWithAuthProvider);
        if (data?.agreeTerms) await applyIPAddressWhenTermsAccepted();
      }

      let successUrl = `/client/manage/${clientId}`;
      const redirectUrl = redirect || originPath || resolveRedirectFromOriginParams();
      if (redirectUrl) successUrl = redirectUrl;

      const tracker = new TrackerService({ isTest: isLocalHost });
      const userId = authUser?.uid;
      tracker.mixPanelIdentify(userId, clientId, data.displayName, data.email);
      tracker.mixpanelTrack('client_signup_complete', {
        distinct_id: userId,
        clientName: client?.displayName,
        clientId,
        ...authProviderFields,
      });
      if (!isLocalHost) {
        tracker.googleTrack('client_signup_complete', { userId, clientId, ...authProviderFields });
      }

      props.history.push(successUrl);
    })();
  }, [authUser, client, model, clientId, redirect, originPath, originReason, originParams, applyIPAddressWhenTermsAccepted, resolveRedirectFromOriginParams, props.history]);

  const cancelLogin = useCallback(async () => {
    await firebase.auth().signOut();
    props.history.push('/');
  }, [props.history]);

  // The veteran's net. Some 10,000 clients only ever signed up with an email, and
  // Firebase has no phone credential to match when they type their number on a
  // link, so a new account was minted. One line here lets them say so: the new
  // shell is flagged (never deleted — states, not deletes) and they go to the
  // login door in email mode with the join or claim kept.
  const loginWithEmailInstead = useCallback(async () => {
    const uid = authUser && authUser.uid;
    setLeaving(true);
    try {
      if (uid) {
        await firebase.firestore().collection('users').doc(uid).set({
          declinedAsDuplicateOf: 'email-account',
          declinedAsDuplicateAt: firebase.firestore.FieldValue.serverTimestamp(),
        }, { merge: true });
      }
    } catch (err) {
      console.warn('[CompleteClientSignup] shell flag write failed:', err && err.message);
    }
    try { await firebase.auth().signOut(); } catch (_) { /* noop */ }
    const back = redirect || originPath || resolveRedirectFromOriginParams();
    const query = `mode=login&entry=email${back ? `&redirect=${encodeURIComponent(back)}` : ''}`;
    props.history.push(`/client/login?${query}`);
  }, [authUser, redirect, originPath, resolveRedirectFromOriginParams, props.history]);

  if (authLoading || clientLoading) {
    return <CenteredSpinner loading={true} noLayout={false} />;
  }

  // Signed out — e.g. the account was deleted or signed out mid-flow (browser back
  // after the invite ask-first "use a different sign-in"). Reading the client doc
  // now can only produce a rules denial ("false for 'get'") and a scary error
  // screen. Bounce to login instead, carrying this page as the redirect.
  if (!authUser) {
    if (leaving) return <CenteredSpinner loading={true} noLayout={false} />;
    const returnTo = `${props.location?.pathname || ''}${props.location?.search || ''}`;
    return <Redirect to={`/client/login?redirect=${encodeURIComponent(returnTo)}`} />;
  }

  // Not your client: a stale ?redirect can carry ANOTHER identity's completeSignup
  // URL across a sign-out/sign-in (e.g. invite ask-first "use a different sign-in",
  // then logging in as an existing user). Any authed user can READ a client doc, so
  // the form would render and then every save would be a rules write-denial at
  // clients L289. Bounce to login with NO redirect — and clear the sessionStorage
  // redirect carriage too, or the login screen would restore the foreign redirect
  // and loop straight back here. The decision tree then routes this user by their
  // own state instead.
  if (!isAdmin && client && client.ownerId && client.ownerId !== authUser.uid) {
    clearSessionForDashboard();
    return <Redirect to="/client/login" />;
  }

  if (!clientId) return <MissingAccountScreen onSignIn={() => props.history.push('/client/login')} />;
  if (!client) {
    return (
      <CouldNotLoadScreen
        message={clientError?.message}
        onReload={() => window.location.reload()}
        onSignIn={() => props.history.push('/client/login')}
      />
    );
  }

  const cancelLink = (
    <button type="button" onClick={cancelLogin} style={{ ...signupTextLink, color: '#8c8c8c' }}>
      Cancel sign up
    </button>
  );

  const veteranLine = !isAdmin && isPhoneOnlyAuthUser(authUser) ? (
    <p style={{ fontSize: 16, color: '#595959', margin: '0 0 16px' }}>
      Used Striketip before with an email?{' '}
      <button
        type="button"
        onClick={loginWithEmailInstead}
        style={{
          background: 'none', border: 'none', padding: 0, color: '#1890ff', fontSize: 16,
          cursor: 'pointer', textDecoration: 'underline',
        }}
      >
        Log in to that account instead
      </button>
    </p>
  ) : null;

  // ── Required step: verify a mobile by SMS. Only Google/Apple/email sign-ups land
  // here — a phone-first sign-up already carries a verified auth phone. Kept as its
  // own focused screen so it's unambiguous that this is a distinct, required step. ──
  if (!isAdmin && !verifiedAuthPhone) {
    return (
      <SignupShell
        title="Verify your mobile"
        lead="This keeps your account secure and lets you sign in with your phone."
        footer={cancelLink}
      >
        <SignupPhoneVerify
          profileCountry={client.country}
          onVerified={() => setPhoneVerifiedTick((t) => t + 1)}
        />
      </SignupShell>
    );
  }

  return (
    <SignupShell
      step={2}
      of={stepsTotal}
      title="About you"
      lead="Your name, what you do, and where you are. You can change these later."
      footer={cancelLink}
    >
      {veteranLine}
      <EditClientProfileForm
        client={client}
        callback={onClickSave}
        isSignUp={true}
        isAdmin={isAdmin}
      />
    </SignupShell>
  );
}
